import React, { createContext, useEffect, useState } from 'react';
import './App.css';
import MenuBar from './Menu/MenuBar';
import GlobalState from './Interfaces/IGlobalState';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { MenuItems } from './Menu/MenuItem';
import { Toaster } from 'react-hot-toast';
import Login from './Login/Login';
import { renewToken } from './Modules/Data';
import Landing from './Landing/Landing';
import { API_URL } from './Globals';
import { endSession } from './Modules/Session';

export const GlobalContext = createContext<{
  globalVariable: GlobalState;
  setGlobalVariable: React.Dispatch<React.SetStateAction<GlobalState>>;
}>({} as any);

const parsePermissions = (permissionsString: string | null): string[] => {
  try {
    return permissionsString ? JSON.parse(permissionsString) : []
  } catch (error) {
    console.error('Error parsing permissions from localStorage:', error)
    return []
  }
}

function App() {
  const [globalVariable, setGlobalVariable] = useState<GlobalState>({
    menuSelection: 'home',
    sessionToken: localStorage.getItem('sessionToken') || undefined,
    sessionValid: localStorage.getItem('sessionValid') ? true : false,
    permissions: parsePermissions(localStorage.getItem('permissions') || ''),
    username: localStorage.getItem('username') || '',
    firstName: localStorage.getItem('firstName') || '',
    lastName: localStorage.getItem('lastName') || ''
  });

  useEffect(() => {
    if (localStorage.getItem('token')) {
      void renewToken().then(user => {
        if (!user) return
        setGlobalVariable(previous => ({
          ...previous,
          sessionToken: user.sessionToken,
          sessionValid: true,
          permissions: user.permissions,
          username: user.username,
          firstName: user.firstName,
          lastName: user.lastName
        }))
      })
    }
  }, []);

  // Register real interactions, not mere open tabs. Reporting is throttled to
  // avoid frequent database writes; the server remains the authority on login.
  useEffect(() => {
    if (!globalVariable.sessionValid || !globalVariable.sessionToken) return

    let lastReportedAt = 0
    let disposed = false
    const reportActivity = () => {
      if (disposed || document.visibilityState !== 'visible') return
      const token = localStorage.getItem('token')
      if (!token) return

      const now = Date.now()
      if (now - lastReportedAt < 45_000) return
      lastReportedAt = now

      void fetch(`${API_URL}/api/user/presence`, {
        method: 'POST',
        headers: { Token: token },
        cache: 'no-store'
      }).then(response => {
        if (disposed) return
        if (response.status === 401) {
          endSession()
        } else if (!response.ok) {
          lastReportedAt = 0
        }
      }).catch(() => { if (!disposed) lastReportedAt = 0 })
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') reportActivity()
    }

    reportActivity()
    window.addEventListener('pointerdown', reportActivity, { passive: true })
    window.addEventListener('keydown', reportActivity)
    window.addEventListener('wheel', reportActivity, { passive: true })
    window.addEventListener('focus', reportActivity)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      disposed = true
      window.removeEventListener('pointerdown', reportActivity)
      window.removeEventListener('keydown', reportActivity)
      window.removeEventListener('wheel', reportActivity)
      window.removeEventListener('focus', reportActivity)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [globalVariable.sessionValid, globalVariable.sessionToken])

  return (
    <GlobalContext.Provider value={{ globalVariable, setGlobalVariable }}>
      <Toaster />
      {globalVariable.sessionValid && (
        <div className={`App app-shell ${globalVariable.menuSelection === 'home' ? 'app-shell-home' : 'app-shell-content'}`}>
          <MenuBar />
          {globalVariable.menuSelection !== 'home' && (
            <div className="pt-16 md:pt-20 md:pb-2">
              <div className="container mx-auto hidden px-4 md:block sm:px-6 lg:px-8">
                <nav className="flex" aria-label="Breadcrumb">
                  <ol className="inline-flex items-center space-x-1 md:space-x-3 mb-1">
                    <li className="inline-flex items-center">
                      <div className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600 dark:text-gray-400 dark:hover:text-white">
                        Sivu
                      </div>
                    </li>
                    <li>
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faAngleRight} className='text-gray-400 mt-1' />
                        <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2 dark:text-gray-400">{(MenuItems.find((mi) => mi.id === globalVariable.menuSelection))?.title}</span>
                      </div>
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          )}
          {(() => {
            const selectedMenuItem = MenuItems.find((mi) => mi.id === globalVariable.menuSelection)
            const hasPermission = selectedMenuItem?.permissions?.some(permission => globalVariable.permissions?.includes(permission))
            if (selectedMenuItem && selectedMenuItem.component && hasPermission) {
              const Component = selectedMenuItem.component
              return <Component />
            }
            return <Landing />
          })()}
        </div>
      )}
      {!globalVariable.sessionValid && (
        <Login globalVariable={setGlobalVariable} />
      )}
    </GlobalContext.Provider>
  );
}

export default App;
